// src/auth.js
// Custom Auth helpers with robust error mapping and CORRECT Cognito session chaining.
//
// verifyCode() now returns either { success: true, tokens }
// OR { success: false, session, challengeName, challengeParameters } when the code was wrong,
// so callers can update the session for the next attempt (avoids "Invalid session" errors).

import { CognitoIdentityProviderClient, SignUpCommand, InitiateAuthCommand, RespondToAuthChallengeCommand } from '@aws-sdk/client-cognito-identity-provider';

const REGION = process.env.REACT_APP_AWS_REGION;
const CLIENT_ID = process.env.REACT_APP_USER_POOL_CLIENT_ID;

if (!REGION || !CLIENT_ID) {
	// Helpful early warning
	// eslint-disable-next-line no-console
	// console.error('[auth] Missing env: REACT_APP_AWS_REGION or REACT_APP_USER_POOL_CLIENT_ID. Check your .env / build config.');
}

const client = new CognitoIdentityProviderClient({ region: REGION });

// Memory tokens (mirrored to localStorage to survive refresh)
let _tokens = {
	idToken: localStorage.getItem('idToken') || '',
	accessToken: localStorage.getItem('accessToken') || '',
	refreshToken: localStorage.getItem('refreshToken') || '',
};

export function getIdToken() {
	return _tokens.idToken || '';
}

/**
 * Decodes a JWT and returns the payload, or null if invalid.
 * JWTs are base64url-encoded: header.payload.signature
 */
function decodeJwtPayload(token) {
	try {
		const parts = token.split('.');
		if (parts.length !== 3) return null;
		// Base64url → Base64 → decode
		const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		const json = atob(base64);
		return JSON.parse(json);
	} catch (e) {
		return null;
	}
}

/**
 * Checks if the current idToken is valid (exists and not expired).
 * If expired, clears all tokens and returns false.
 */
export function isAuthed() {
	const token = _tokens.idToken;
	if (!token) return false;

	const payload = decodeJwtPayload(token);
	if (!payload || !payload.exp) {
		// Invalid token structure - clear it
		signOut();
		return false;
	}

	// JWT exp is in seconds, Date.now() is in milliseconds
	const nowSeconds = Math.floor(Date.now() / 1000);
	if (payload.exp < nowSeconds) {
		// Token expired - clear it
		// eslint-disable-next-line no-console
		// console.info('[auth] Token expired, clearing session');
		signOut();
		return false;
	}

	return true;
}
export function signOut() {
	_tokens = { idToken: '', accessToken: '', refreshToken: '' };
	localStorage.removeItem('idToken');
	localStorage.removeItem('accessToken');
	localStorage.removeItem('refreshToken');
}

function strongTempPassword() {
	// 20-char, includes upper/lower/number/special to satisfy strict policies.
	const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
	const lower = 'abcdefghijkmnopqrstuvwxyz';
	const nums = '23456789';
	const special = '!@#$%^&*()-_=+[]{}';
	const all = upper + lower + nums + special;

	function pick(set) {
		return set[Math.floor(Math.random() * set.length)];
	}
	const must = [pick(upper), pick(lower), pick(nums), pick(special)];
	const rest = Array.from({ length: 16 }, () => pick(all));
	const combined = [...must, ...rest].sort(() => Math.random() - 0.5);
	return combined.join('');
}

function mapAwsError(e) {
	const code = e?.name || e?.Code || 'UnknownError';
	const status = e?.$metadata?.httpStatusCode;
	const msg = e?.message || e?.Message || String(e);
	return { code, status, message: msg };
}

/**
 * Initiates the custom auth flow (sends or reuses an OTP).
 * Returns { session, cooldownSeconds, sendsRemaining, rateLimited, retryAfterSeconds }
 */
export async function sendCode(phoneE164) {
	const username = phoneE164.trim();
	// eslint-disable-next-line no-console
	// console.info('[auth] sendCode →', username);

	// 1) Try a best-effort SignUp to ensure the user exists. Ignore if already exists.
	try {
		await client.send(
			new SignUpCommand({
				ClientId: CLIENT_ID,
				Username: username,
				Password: strongTempPassword(),
				UserAttributes: [{ Name: 'phone_number', Value: username }],
			})
		);
		// eslint-disable-next-line no-console
		// console.info('[auth] SignUp ok (or will be auto-confirmed by your PreSignUp lambda)');
	} catch (e) {
		const { code, status, message } = mapAwsError(e);
		if (code === 'UsernameExistsException') {
			// eslint-disable-next-line no-console
			// console.info('[auth] SignUp skipped (user exists)');
		} else if (code === 'InvalidPasswordException') {
			// This shouldn't happen with our generator, but log clearly just in case.
			// eslint-disable-next-line no-console
			// console.warn(`[auth] SignUp password policy failed (${status}): ${message}`);
		} else if (code !== 'InvalidParameterException') {
			// Some regions complain about duplicate attrs; we can continue to InitiateAuth regardless.
			// eslint-disable-next-line no-console
			// console.warn(`[auth] SignUp non-fatal (${code}, ${status}): ${message}`);
		}
	}

	// 2) Initiate CUSTOM_AUTH (Lambda enforces cooldown & rate limits)
	let init;
	try {
		init = await client.send(
			new InitiateAuthCommand({
				ClientId: CLIENT_ID,
				AuthFlow: 'CUSTOM_AUTH',
				AuthParameters: { USERNAME: username },
			})
		);
	} catch (e) {
		const { code, status, message } = mapAwsError(e);
		// eslint-disable-next-line no-console
		// console.error(`[auth] InitiateAuth failed (${code}, ${status}): ${message}`);
		throw new Error(message || 'Could not start login. Please try again.');
	}

	if (init.ChallengeName !== 'CUSTOM_CHALLENGE' || !init.Session) {
		// eslint-disable-next-line no-console
		// console.error('[auth] Unexpected InitiateAuth state:', init);
		throw new Error('Unexpected auth state');
	}

	const p = init.ChallengeParameters || {};
	const cooldownSeconds = Number(p.cooldown || 0);
	const sendsRemaining = typeof p.sends_remaining !== 'undefined' ? Number(p.sends_remaining) : undefined;
	const rateLimited = typeof p.rate_limited !== 'undefined' ? String(p.rate_limited).toLowerCase() === 'true' || p.rate_limited === '1' : false;
	const retryAfterSeconds = Number(p.retry_after || 0);

	// eslint-disable-next-line no-console
	// console.info('[auth] InitiateAuth → session obtained. cooldown:', cooldownSeconds, 'sendsRemaining:', sendsRemaining, 'rateLimited:', rateLimited);

	return {
		session: init.Session,
		cooldownSeconds,
		sendsRemaining,
		rateLimited,
		retryAfterSeconds,
	};
}

/**
 * Submits an OTP. IMPORTANT: if the code is wrong, Cognito returns a NEW Session.
 * We return an object the caller can inspect, and they MUST update their session.
 *
 * On success: { success: true, tokens }
 * On more challenge: { success: false, session, challengeName, challengeParameters }
 * On fatal error: throws Error
 */
export async function verifyCode({ phoneE164, session, otp }) {
	const username = phoneE164.trim();
	// eslint-disable-next-line no-console
	// console.info('[auth] verifyCode →', username, 'session:', !!session);

	let resp;
	try {
		resp = await client.send(
			new RespondToAuthChallengeCommand({
				ClientId: CLIENT_ID,
				ChallengeName: 'CUSTOM_CHALLENGE',
				Session: session,
				ChallengeResponses: {
					USERNAME: username,
					ANSWER: otp.trim(),
				},
			})
		);
	} catch (e) {
		const { code, status, message } = mapAwsError(e);
		// eslint-disable-next-line no-console
		// console.error(`[auth] RespondToAuthChallenge failed (${code}, ${status}): ${message}`);
		throw new Error(message || 'Verification failed');
	}

	// Case 1: Success → AuthenticationResult contains tokens
	const r = resp.AuthenticationResult;
	if (r?.IdToken) {
		_tokens.idToken = r.IdToken;
		_tokens.accessToken = r.AccessToken || '';
		_tokens.refreshToken = r.RefreshToken || '';
		localStorage.setItem('idToken', _tokens.idToken);
		localStorage.setItem('accessToken', _tokens.accessToken);
		if (_tokens.refreshToken) localStorage.setItem('refreshToken', _tokens.refreshToken);
		// eslint-disable-next-line no-console
		// console.info('[auth] verifyCode → SUCCESS (tokens stored)');
		return { success: true, tokens: r };
	}

	// Case 2: Still challenged (wrong code or more steps) → MUST chain new Session
	if (resp.ChallengeName === 'CUSTOM_CHALLENGE' && resp.Session) {
		// eslint-disable-next-line no-console
		// console.warn('[auth] verifyCode → wrong/needs-more. New session issued.');
		return {
			success: false,
			session: resp.Session,
			challengeName: resp.ChallengeName,
			challengeParameters: resp.ChallengeParameters || {},
		};
	}

	// Unexpected state
	// eslint-disable-next-line no-console
	// console.error('[auth] verifyCode → unexpected response:', resp);
	throw new Error('Unexpected auth state');
}
